import Title from "common/Typography/Title.tsx";
import PayoutMethodCard from "features/payouts/PayoutMethods/PayoutMethodCard.tsx";
import React, { useState } from "react";
import Flex from "common/Boxes/Flex.tsx";
import Link from "common/Typography/Link.tsx";
import CreateOrEditPayoutMethodModal from "features/payouts/PayoutMethods/CreateOrEditPayoutMethodModal.tsx";
import EmptyView from "common/Empty/EmptyView.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

import {
  LanguageNameSpaces,
  PayoutMethod,
  PayoutMethodSource,
} from "@shared/types";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import styled, { useTheme } from "styled-components";
import { Plus } from "lucide-react";
import Button from "common/Buttons/Button.tsx";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import { CardHeader, CardTitle } from "features/affiliation/NewLook/parts.tsx";
import { InlineEmpty } from "features/affiliation/NewLook/NewLookCharts.tsx";
import TablePanel from "features/admin/NewLook/TablePanel";
import Skeleton from "common/NewLook/Skeleton.tsx";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";

interface PayoutMethodsProps {
  payoutMethods?: PayoutMethod[];
  loading?: boolean;
  inModal?: boolean;
  /** New look: render as its own flush panel (title and add in the header). */
  asPanel?: boolean;
  source: PayoutMethodSource;
}

const PayoutMethods = ({
  payoutMethods,
  loading,
  inModal,
  asPanel,
  source,
}: PayoutMethodsProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const showEmptyState = !payoutMethods?.length && !loading;
  const [createPayoutModalVisible, setCreatePayoutModalVisible] =
    useState(false);
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const theme = useTheme();
  const { isNewLook } = theme;

  const buttonData = {
    buttonText: t("payouts.add_method"),
    buttonIcon: <PlusOutlined />,
    buttonOnClick: () => setCreatePayoutModalVisible(true),
  };

  const emptyState = (
    <EmptyView
      iconKey={EmptyStatesType.PayoutMethods}
      iconWidth={isMobile ? 100 : 120}
      title={t("payouts.no_payout_methods")}
      description={t("payouts.payout_methods_description")}
      {...buttonData}
    />
  );

  const addMethodButton = (
    <Link onClick={buttonData.buttonOnClick}>+ {buttonData.buttonText}</Link>
  );

  const createOrEditModal = (
    <CreateOrEditPayoutMethodModal
      isVisible={createPayoutModalVisible}
      closeModal={() => setCreatePayoutModalVisible(false)}
      source={source}
    />
  );

  // New look panel: the methods as rows under the panel title, "Add a
  // method" in the header; skeleton rows while they load.
  if (isNewLook && asPanel) {
    let body: React.ReactNode;
    if (loading && !payoutMethods?.length)
      body = [0, 1].map((index) => (
        <PanelSkeletonRow key={index}>
          <Skeleton width={32} height={32} radius={8} />
          <PanelSkeletonText>
            <Skeleton width={120} height={12} />
            <Skeleton width={160} height={10} />
          </PanelSkeletonText>
        </PanelSkeletonRow>
      ));
    else if (showEmptyState)
      body = (
        <PanelEmpty
          title={t("payouts.no_payout_methods")}
          description={t("payouts.payout_methods_description")}
          action={
            <Button onClick={buttonData.buttonOnClick}>
              <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
              {buttonData.buttonText}
            </Button>
          }
        />
      );
    else
      body = payoutMethods?.map((method) => (
        <PayoutMethodCard
          payoutMethod={method}
          key={method.payout_method_id}
          inPanel={true}
          source={source}
        />
      ));
    return (
      <TablePanel
        title={t("payouts.my_payout_methods")}
        count={payoutMethods?.length || undefined}
        actions={
          !showEmptyState && (
            <Button size="small" onClick={buttonData.buttonOnClick}>
              <Plus size={14} strokeWidth={NEW_LOOK_ICON_STROKE} />
              {buttonData.buttonText}
            </Button>
          )
        }
        flush={true}
      >
        <PanelList>{body}</PanelList>
        {createOrEditModal}
      </TablePanel>
    );
  }

  if (isNewLook) {
    const addButton = (
      <AddMethodButton
        type="text"
        onClick={buttonData.buttonOnClick}
        $block={inModal}
      >
        <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
        {buttonData.buttonText}
      </AddMethodButton>
    );
    return (
      <LoadingSpinnerOverlay
        isLoading={loading}
        wrap={true}
        spinnerMarginTop={60}
        backgroundColor={theme.colors.cardBgColor}
      >
        {!inModal && (
          <CardHeader>
            <CardTitle>{t("payouts.my_payout_methods")}</CardTitle>
            {!showEmptyState && addButton}
          </CardHeader>
        )}
        {showEmptyState ? (
          <NewLookEmpty>
            <InlineEmpty
              iconKey={EmptyStatesType.PayoutMethods}
              title={t("payouts.no_payout_methods")}
              action={
                <>
                  <EmptyDescription>
                    {t("payouts.payout_methods_description")}
                  </EmptyDescription>
                  <Button onClick={buttonData.buttonOnClick}>
                    <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                    {buttonData.buttonText}
                  </Button>
                </>
              }
            />
          </NewLookEmpty>
        ) : (
          <NewLookList>
            {payoutMethods?.map((method) => (
              <PayoutMethodCard
                payoutMethod={method}
                key={method.payout_method_id}
                inModal={inModal}
                source={source}
              />
            ))}
          </NewLookList>
        )}
        {inModal && !showEmptyState && addButton}
        {createOrEditModal}
      </LoadingSpinnerOverlay>
    );
  }

  return (
    <LoadingSpinnerOverlay
      isLoading={loading}
      wrap={true}
      spinnerMarginTop={100}
    >
      {!inModal && (
        <Flex style={{ marginBottom: 16 }}>
          <Title level={4}>{t("payouts.my_payout_methods")}</Title>
          {!showEmptyState && addMethodButton}
        </Flex>
      )}
      <Flex
        $flexDirection="column"
        style={{ gridGap: 16, marginBottom: inModal ? 16 : 0 }}
      >
        {showEmptyState
          ? emptyState
          : payoutMethods?.map((method) => (
              <PayoutMethodCard
                payoutMethod={method}
                key={method.payout_method_id}
                inModal={inModal}
                source={source}
              />
            ))}
      </Flex>
      {inModal && !showEmptyState && addMethodButton}
      {createOrEditModal}
    </LoadingSpinnerOverlay>
  );
};

export default PayoutMethods;

const AddMethodButton = styled(Button)<{ $block?: boolean }>`
  &.ant-btn {
    height: 32px;
    padding: 0 12px;
    gap: 6px;
    color: ${(p) => p.theme.colors.primaryBlue};
    font-size: 13px;
    font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
    /* Overhangs the 28px card header line. */
    margin-block: -2px;
    ${(p) =>
      p.$block &&
      `
      width: 100%;
      height: 44px;
      margin: 4px 0 0;
      border: 1px dashed ${p.theme.colors.cardBorderColor};
      border-radius: 12px;
    `}
  }
`;
const NewLookList = styled.div`
  display: flex;
  flex-direction: column;
`;
/* 24px from the header above and from the card's edge below. */
const NewLookEmpty = styled.div`
  padding: 12px 0 16px;
`;
const EmptyDescription = styled.div`
  max-width: 260px;
  margin: -6px 0 6px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
  line-height: 1.5;
`;
const PanelList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
`;
const PanelSkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 60px;
  padding: 8px 20px;
`;
const PanelSkeletonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
